<template>
  <hl-Modal dismisable @close="toggleInvoiceDetailsModal(false)">
    <!-- Loader -->
    <div v-if="loading.order" class="text-center py-5">
      <div class="spinner-grow text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <!-- / Loader -->
    <div v-else class="text-left">
      <div class="border-status bg-absolute" :class="'border-status-' + getBorderStatus(order.status).colorClass">
        <h3 class="text-center">Faktura - {{ order.period }}</h3>

        <div class="gridScalable mt-4">
          <div class="gridScalable__flex">
            <span>Fakturadato</span>
            <hl-Date :date="order.date.invoice" />
          </div>
          <div class="gridScalable__flex">
            <span>Beløp</span>
            <hl-Currency :amount="order.amount.invoiced" />
          </div>
        </div>
        <div class="gridScalable">
          <div class="gridScalable__flex">
            <span>Forfallsdato</span>
            <hl-Date :date="order.date.due" />
          </div>
          <div class="gridScalable__flex">
            <span>Status</span>
            {{ getPaymentStatus(order.status).value || order.status }}
          </div>
        </div>
      </div>

      <ul class="nav nav-tabs mt-5">
        <li class="nav-item cursor-pointer" @click="setActiveTab('trail')">
          <a class="nav-link" :class="{ active: activeTab === 'trail' }">Hendelser</a>
        </li>
        <li class="nav-item cursor-pointer" @click="setActiveTab('details')">
          <a class="nav-link" :class="{ active: activeTab === 'details' }">Detaljer</a>
        </li>
        <li
          v-if="isActionsTabVisible"
          class="nav-item cursor-pointer"
          @click="setActiveTab('actions')"
        >
          <a class="nav-link" :class="{ active: activeTab === 'actions' }">Handlinger</a>
        </li>
      </ul>

      <div class="tab-content bg-light mt-0 pb-5 bg-absolute tabs">
        <div v-if="activeTab === 'trail'">
          <ListHeader>
            <div class="col">Hendelse</div>
            <div class="col">Dato</div>
            <div class="col">Sum</div>
          </ListHeader>

          <ListItem v-for="(event, i) in order.events" :key="i">
            <div class="col">{{ eventMap[event.event] || event.event }}</div>
            <div class="col">
              <hl-Date :date="event.date"></hl-Date>
            </div>
            <div class="col">
              <hl-Currency v-if="event.amount" :amount="event.amount"></hl-Currency>
            </div>
          </ListItem>

          <ListHeader>
            <div class="col font-weight-bold">Skyldig beløp</div>
            <div class="col"></div>
            <div class="col font-weight-bold">
              <hl-Currency :amount="order.amount.unpaid" />
            </div>
          </ListHeader>
        </div>

        <div v-if="activeTab === 'details'">
          <div class="gridScalable">
            <div class="gridScalable__flex">
              <span>Fakturanummer</span>
              <span class="font-weight-bold">{{ order.invoiceNumber }}</span>
            </div>
            <div class="gridScalable__flex">
              <span>Bankkonto</span>
              <span class="font-weight-bold">{{ order.bankAccountNumber }}</span>
            </div>
          </div>
          <div class="gridScalable">
            <div class="gridScalable__flex">
              <span>Kundenummer</span>
              <span class="font-weight-bold">{{ order.customerId }}</span>
            </div>
            <div class="gridScalable__flex">
              <span>KID</span>
              <span class="font-weight-bold">{{ order.structuredReference }}</span>
            </div>
          </div>

          <div class="gridThreeCols mt-5">
            <span>Beskrivelse</span>
            <div class="d-flex justify-content-center">
              <span>MVA*</span>
            </div>
            <div class="d-flex justify-content-end">
              <span>Beløp</span>
            </div>
          </div>

          <hr />

          <div v-for="row in order.rows" :key="row.id" class="gridThreeCols">
            <span>{{ row.text }}</span>
            <div class="d-flex justify-content-center">
              <span>{{ row.vat }}%</span>
            </div>
            <div class="d-flex justify-content-end">
              <hl-Currency :amount="row.amount" />
            </div>
          </div>

          <div class="gridScalable">
            <span>Sum uten MVA*</span>
            <div class="d-flex justify-content-end">
              <hl-Currency :amount="calculateDetailsTotalWithoutVAT" />
            </div>
          </div>

          <div class="gridScalable">
            <span>Sum MVA*</span>
            <div class="d-flex justify-content-end">
              <hl-Currency :amount="calculateDetailsTotalVAT" />
            </div>
          </div>

          <hr />

          <div class="gridScalable">
            <span>Totalt å betale</span>
            <div class="d-flex justify-content-end">
              <hl-Currency :amount="calculateDetailsTotalWithVAT" />
            </div>
          </div>

          <div class="invoices mt-4">
            <a
              v-for="document in order.documents"
              :key="document.orderId"
              :href="document.uri ? document.uri.uri : null"
              target="_blank"
            >
              <FileThumbnail contentType="application/pdf" :fileName="`${document.invoiceNumber}-${document.typeOf}`" />
            </a>
          </div>
        </div>

        <div v-if="activeTab === 'actions'">
          <div v-if="!chosenInvoiceAction">
            <div class="row text-center my-4">
              <hl-RadioButtonTapWithIcon
                class="col"
                iconClass="budicon-bill-tick"
                layout="vertical"
                radioValue="credit"
                :disabled="isOrderPaid"
                v-model="chosenInvoiceAction"
                @click.native="openCreditNoteIfAllowed"
              >
                <p class="font-weight-bold">Krediter faktura</p>
                <p v-if="isOrderPaid">Ikke mulig siden faktura er oppgjort.</p>
              </hl-RadioButtonTapWithIcon>

              <hl-RadioButtonTapWithIcon
                class="col"
                iconClass="budicon-invoice"
                layout="vertical"
                radioValue="directPayment"
                v-model="chosenInvoiceAction"
                :disabled="isOrderPaid"
              >
                <p class="font-weight-bold">Registrer direktebetaling</p>
                <p v-if="isOrderPaid">Ikke mulig siden faktura er oppgjort.</p>
              </hl-RadioButtonTapWithIcon>
            </div>

            <div class="row text-center my-4">
              <!-- Payment reminders are stopped. Reactivate them -->
              <hl-RadioButtonTapWithIcon
                v-if="order.status.remindersStopped"
                class="col"
                iconClass="budicon-cross-sign"
                layout="vertical"
                radioValue="stopPayment"
                v-model="chosenInvoiceAction"
                :disabled="isStopRemindersDisabled || isOrderPaid"
              >
                <p class="font-weight-bold">Gjenoppta betalingsoppfølging</p>
                <p v-if="isStopRemindersDisabled">Ikke mulig siden overgått forfall m/ 25 dager.</p>
                <p v-else-if="isOrderPaid">Ikke mulig siden faktura er oppgjort.</p>
              </hl-RadioButtonTapWithIcon>

              <!-- Payment reminders are ongoing. Deactivate them -->
              <hl-RadioButtonTapWithIcon
                v-else
                class="col"
                iconClass="budicon-cross-sign"
                layout="vertical"
                radioValue="stopPayment"
                v-model="chosenInvoiceAction"
                :disabled="isStopRemindersDisabled || isOrderPaid"
              >
                <p class="font-weight-bold">Stopp betalingsoppfølging</p>
                <p v-if="isStopRemindersDisabled">Ikke mulig siden overgått forfall m/ 25 dager.</p>
                <p v-else-if="isOrderPaid">Ikke mulig siden faktura er oppgjort.</p>
              </hl-RadioButtonTapWithIcon>

              <hl-RadioButtonTapWithIcon
                class="col"
                iconClass="budicon-sending-message"
                layout="vertical"
                radioValue="sendInvoice"
                v-model="chosenInvoiceAction"
              >
                <p class="font-weight-bold">Send fakturakopi</p>
              </hl-RadioButtonTapWithIcon>
            </div>
          </div>

          <!-- Direct Payment -->
          <InvoiceTrailDirectPayment
            v-if="chosenInvoiceAction === 'directPayment'"
            :landlordId="landlordId"
            :tenancyId="tenancyId"
            :orderId="orderId"
            @back="resetChosenAction"
            @newPayment="$emit('newDirectPayment')"
          />

          <!-- Stop Payment -->
          <InvoiceTrailStopPayment
            v-if="chosenInvoiceAction === 'stopPayment'"
            :landlordId="landlordId"
            :tenancyId="tenancyId"
            :orderId="orderId"
            :remindersStopped="order.status.remindersStopped"
            @back="resetChosenAction"
          />

          <!-- Send Invoice -->
          <InvoiceTrailSendInvoice
            v-if="chosenInvoiceAction === 'sendInvoice'"
            :invoiceNumber="order.invoiceNumber"
            :landlordId="landlordId"
            :tenancyId="tenancyId"
            :orderId="orderId"
            @back="resetChosenAction"
          />
        </div>
      </div>
    </div>
  </hl-Modal>
</template>

<script>
import Vue from 'vue';
import FileThumbnail from '@/components/UI/FileThumbnail';
import InvoiceTrailDirectPayment from '@/components/tenancy/modals/invoiceTrail/InvoiceTrailDirectPayment';
import InvoiceTrailSendInvoice from '@/components/tenancy/modals/invoiceTrail/InvoiceTrailSendInvoice';
import InvoiceTrailStopPayment from '@/components/tenancy/modals/invoiceTrail/InvoiceTrailStopPayment';

import ListHeader from '@/components/UI/list/ListHeader';
import ListItem from '@/components/UI/list/ListItem';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { TenancyAccountingApi } from '@modules/husleieapi2';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import RadioButtonTapWithIcon from '@modules/husleie-framework/src/components/forms/inputs/radio-buttons/RadioButtonTapWithIcon.vue';

export default {
  components: {
    FileThumbnail,
    InvoiceTrailDirectPayment,
    InvoiceTrailSendInvoice,
    InvoiceTrailStopPayment,
    ListHeader,
    ListItem,
    'hl-Modal': Modal,
    'hl-Date': Date,
    'hl-Currency': Currency,
    'hl-RadioButtonTapWithIcon': RadioButtonTapWithIcon,
  },

  props: {
    initialPage: {
      type: String,
      default: 'trail',
    },

    landlordId: {
      type: [Number, String],
    },

    tenancyId: {
      type: [Number, String],
    },

    orderId: {
      type: [Number, String],
    },
    invoiceTrailChosenInvoiceAction: {
      type: String,
      default: null,
    },
    isActionsTabVisible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeTab: 'trail',
      chosenInvoiceAction: null,
      eventMap: {
        directPayment: 'Direktebetaling',
        creditNote: 'Kreditnota',
        credit_note: 'Kreditnota',
        invoice: 'Faktura',
        reminder1: 'Første påminnelse',
        reminder2: 'Andre påminnelse',
        reminder3: 'Varsel om fravikelse',
        payment: 'Betaling',
        disbursement: 'Utbetaling',
      },
      order: {
        amount: {},
        date: {},
      },
      loading: {
        order: true,
      },
    };
  },

  computed: {
    ...mapGetters('tenancy', ['getTenancyDetailedInvoice']),

    details() {
      return this.getTenancyDetailedInvoice;
    },

    calculateDetailsTotalWithoutVAT() {
      let total = 0;
      for (const row of this.order.rows) {
        if (row.amount) {
          total += row.amount;
        }
      }
      return total;
    },

    calculateDetailsTotalVAT() {
      let total = 0;
      for (const row of this.order.rows) {
        if (row.amount && row.vat) {
          total += (row.amount * row.vat) / 100;
        }
      }
      return total;
    },

    calculateDetailsTotalWithVAT() {
      return this.calculateDetailsTotalWithoutVAT + this.calculateDetailsTotalVAT;
    },

    // disabled actions
    isOrderPaid() {
      return this.order.status.payment === 'paid';
    },

    isStopRemindersDisabled() {
      return this.order.status.paidDaysDelta > 24;
    },
  },

  methods: {
    ...mapMutations('tenancy', ['toggleCreditNoteModal']),
    ...mapActions('tenancy', ['toggleInvoiceDetailsModal', 'fetchTenancyOrder']),

    getPaymentStatus(status) {
      if (status == null) return;
      if (status.order === 'draft') {
        return {
          colorClass: 'secondary',
          value: 'Utkast',
        };
      }
      if (status.order === 'canceled' || status.payment === 'canceled') {
        return {
          colorClass: 'danger',
          value: 'Kansellert',
        };
      }
      if (status.payment === 'unpaid') {
        return {
          colorClass: 'danger',
          value: 'Ubetalt',
        };
      }

      if (status.payment === 'paid') {
        return {
          colorClass: 'primary',
          value: 'Oppgjort',
        };
      }
      return;
    },

    getBorderStatus(status) {
      const defaultClass = { colorClass: 'light' };
      const primary = { colorClass: 'primary' };
      const secondary = { colorClass: 'secondary' };
      const danger = { colorClass: 'danger' };
      const warning = { colorClass: 'warning' };

      if (status.order == null || status.payment == null) return defaultClass;
      if (status.order === 'draft') return secondary;
      if (status.payment === 'paid') return primary;
      if (status.payment === 'unpaid' && status.paidDaysDelta < -15) return danger;
      if (status.payment === 'unpaid' && status.paidDaysDelta < 0 && status.paidDaysDelta >= -15) return warning;
      return defaultClass;
    },

    async fetchOrder() {
      try {
        this.loading.order = true;
        this.order = await this.fetchTenancyOrder({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          orderId: this.orderId,
        });
        this.loading.order = false;
        this.fetchOrderPDFUri();
      } catch (err) {
        console.error(err);
        this.$toast('Kunne ikke hente ordre.', 'error');
      }
    },

    fetchOrderPDFUri() {
      const documents = this.order.documents;
      const api = new TenancyAccountingApi();

      if (documents == null || !documents.length) {
        return;
      }
      documents.map(async document => {
        const uri = await api.getOneLandlordTenancyAccountingOrderPdfUri(
          this.landlordId,
          this.tenancyId,
          document.orderId
        );
        Vue.set(document, 'uri', uri);
      });
    },

    setActiveTab(tab) {
      this.activeTab = tab;
    },

    resetChosenAction() {
      this.chosenInvoiceAction = null;
      this.fetchOrder();
    },

    openCreditNoteIfAllowed() {
      if (this.isOrderPaid) {
        return null;
      }

      this.closeInvoiceModalAndOpenCreditNote();
    },

    closeInvoiceModalAndOpenCreditNote() {
      this.toggleInvoiceDetailsModal(false);
      this.$nextTick(() => {
        this.toggleCreditNoteModal(true);
      });
    },
  },

  mounted() {
    this.activeTab = this.initialPage;
    if (this.invoiceTrailChosenInvoiceAction !== null) {
      this.chosenInvoiceAction = this.invoiceTrailChosenInvoiceAction;
    }

    this.fetchOrder();
  },
};
</script>

<style scoped lang="scss">
.bg-absolute {
  margin-left: -3rem;
  margin-bottom: -3rem;
  margin-top: -3rem;
  width: calc(100% + 6rem);
  padding: 3rem;
}

.border-status {
  border-left-width: 8px;
  border-left-style: solid;
  position: relative;
  z-index: -1;
  left: -1px;
  top: -1px;
  border-top-left-radius: $border-radius;
}

.border-status-light {
  border-left-color: $gray-300;
}

.border-status-primary {
  border-left-color: $primary;
}

.border-status-danger {
  border-left-color: $danger;
}

.border-status-secondary {
  border-left-color: $secondary;
}

.border-status-warning {
  border-left-color: $warning;
}

.nav {
  border-top: 1px solid $gray-300;
  padding: 0.5rem 3rem;
  margin-left: -3rem;
  position: relative;
  top: -1px;
  width: calc(100% + 6rem);
}

.reset-actions {
  i {
    font-size: 1rem !important;
  }
}
.tabs {
  min-height: 500px;
}

.gridScalable {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0rem 2rem;
  text-align: left;
}
.gridScalable__flex {
  display: flex;
  justify-content: space-between;
}

.gridThreeCols {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0rem 2rem;
  text-align: left;
}

.invoices {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 150px));
  gap: 1rem;
}
</style>
